import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { ShieldCheck, Warning, CheckCircle, Prohibit } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { verifyAttestation } from '../../services/cmcApi';
import { fmtDate } from '../../components/cmc/CmcCommon';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_objectif-ecosystem/artifacts/4tz74flx_LOGO%20OBJECTIF%20SCOP%20OUTREMER%202%20%283%29.png';

export default function CmcVerifyPublicPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await verifyAttestation(token);
        if (mounted) setData(res.data);
      } catch (e) {
        if (mounted) setError(e.response?.data?.detail || 'Document introuvable');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const isRevoked = data?.status === 'revoked';
  const isIssued = data?.status === 'issued';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F9F8F5] to-[#EEF1EC] flex flex-col items-center px-4 py-12" data-testid="cmc-verify-public-page">
      <div className="w-full max-w-xl">
        <div className="flex items-center justify-center mb-8">
          <img src={LOGO_URL} alt="Objectif SCOP Outremer" className="h-14" />
        </div>

        <h1 className="text-center text-2xl font-semibold text-[#1A2421] mb-2" style={{ fontFamily: 'Manrope' }}>
          Vérification d'attestation CMC
        </h1>
        <p className="text-center text-sm text-[#5E6D66] mb-8">
          Caisse Mutualisée des Coopérateurs O'SCOP
        </p>

        {loading && (
          <Card className="border-[#E8E6E1]">
            <CardContent className="py-16 flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
              <p className="text-sm text-[#5E6D66]">Vérification du document…</p>
            </CardContent>
          </Card>
        )}

        {!loading && error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-10 text-center" data-testid="verify-error">
              <Warning size={42} className="mx-auto text-red-500 mb-3" weight="fill" />
              <h2 className="text-lg font-semibold text-red-800 mb-1">Document introuvable</h2>
              <p className="text-sm text-red-700">
                Le jeton de vérification est invalide ou expiré.
              </p>
              <p className="text-xs text-red-600 mt-2 font-mono break-all">{error}</p>
            </CardContent>
          </Card>
        )}

        {!loading && data && (
          <Card className={`border-2 ${isRevoked ? 'border-red-300' : isIssued ? 'border-emerald-300' : 'border-amber-300'}`}>
            <CardContent className="p-8">
              {/* Status banner */}
              <div className={`flex items-center justify-center gap-2 px-4 py-3 rounded-md mb-6 ${
                isRevoked
                  ? 'bg-red-50 text-red-800'
                  : isIssued
                  ? 'bg-emerald-50 text-emerald-800'
                  : 'bg-amber-50 text-amber-800'
              }`} data-testid="verify-status-banner">
                {isRevoked ? (
                  <><Prohibit size={20} weight="fill" /> <span className="font-semibold">Document révoqué</span></>
                ) : isIssued ? (
                  <><CheckCircle size={20} weight="fill" /> <span className="font-semibold">Document authentique</span></>
                ) : (
                  <><Warning size={20} weight="fill" /> <span className="font-semibold">Statut : {data.status}</span></>
                )}
              </div>

              {/* Document type */}
              <div className="text-center mb-6">
                <ShieldCheck size={36} className="mx-auto text-[#2D5A4C] mb-2" weight="fill" />
                <h2 className="text-lg font-semibold text-[#1A2421]">{data.document_type || 'Attestation CMC'}</h2>
              </div>

              {/* Details */}
              <div className="space-y-3 text-sm" data-testid="verify-details">
                <Row label="N° d'attestation" value={data.attestation_number} mono />
                <Row label="Émetteur" value={data.issuer} />
                <Row label="Bénéficiaire" value={data.beneficiary_display} />
                <Row label="Montant" value={data.amount_display} />
                <Row label="Émise le" value={fmtDate(data.issued_at)} />
                {data.document_hash && (
                  <Row label="Empreinte SHA-256" value={`${data.document_hash.slice(0, 24)}…`} mono small />
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-[#F0EFEB] text-center">
                <p className="text-[11px] text-[#5E6D66] italic">
                  {data.privacy || 'Vérification minimale et masquée — RGPD'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-[11px] text-[#5E6D66] mt-6">
          OBJECTIF SCOP OUTREMER · Système de vérification publique CMC
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, mono, small }) {
  return (
    <div className="flex justify-between items-start gap-3 py-1.5 border-b border-[#F0EFEB] last:border-0">
      <span className="text-[11px] uppercase tracking-wide text-[#5E6D66] flex-shrink-0">{label}</span>
      <span className={`text-right text-[#1A2421] font-medium ${mono ? 'font-mono' : ''} ${small ? 'text-xs' : ''}`}>
        {value || '-'}
      </span>
    </div>
  );
}
